import { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  useStaffMember,
  useUpdateStaffMember,
  useDeleteStaffMember,
} from "src/data/organization/queries/staff";
import { useOrganizationRoles } from "src/data/organization/queries/staffRole";
import { isOrganizationManageEnabled } from "src/helpers/FeatureCapabilityConfig";
import { InfoCard } from "src/design/InfoCard";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { SnackBar } from "src/design/SnackBar";
import {
  colorTextPrimary,
  colorTextMuted,
  colorActionDanger,
  colorSurfaceDangerHover,
  colorTextError,
} from "src/design/tokens";

const StaffView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { staffId } = useParams<{ staffId: string }>();
  const { user } = useContext(UserContext);
  const organizationId = user?.organizationId ?? null;
  const canManage = isOrganizationManageEnabled(user);

  const { data: staff, isLoading } = useStaffMember(staffId);
  const { data: roles } = useOrganizationRoles();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [roleId, setRoleId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (staff) {
      setFirstName(staff.firstName);
      setLastName(staff.lastName);
      setEmail(staff.email);
      setPhone(staff.phone);
      setRoleId(staff.roleId);
    }
  }, [staff?.id]);

  const updateMutation = useUpdateStaffMember(() => {
    setSnack({
      open: true,
      message: t("myOrganization.staffView.updateSuccess"),
      severity: "success",
    });
  });

  const deleteMutation = useDeleteStaffMember(organizationId, () => {
    navigate(-1);
  });

  const handleSave = () => {
    if (!staffId) return;
    updateMutation.mutate(
      {
        id: staffId,
        payload: { firstName, lastName, email, phone, roleId },
      },
      {
        onError: (err) =>
          setSnack({
            open: true,
            message:
              err instanceof Error
                ? err.message
                : t("myOrganization.staffView.updateError"),
            severity: "error",
          }),
      },
    );
  };

  const handleDelete = () => {
    if (!staffId) return;
    deleteMutation.mutate(staffId, {
      onError: (err) =>
        setSnack({
          open: true,
          message:
            err instanceof Error
              ? err.message
              : t("myOrganization.staffView.deleteError"),
          severity: "error",
        }),
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!staff) return null;

  const fullName = `${staff.firstName} ${staff.lastName}`;

  return (
    <>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: colorTextPrimary }}
          >
            {fullName}
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={8}>
            <InfoCard
              title={t("myOrganization.staffView.pageTitle")}
              icon={<PersonOutlineIcon fontSize="small" />}
              action={
                canManage ? (
                  <CustomButton
                    color="inverted"
                    startIcon={
                      deleteMutation.isPending ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <DeleteOutlineIcon />
                      )
                    }
                    disabled={deleteMutation.isPending}
                    onClick={() => setDeleteOpen(true)}
                    sx={{
                      color: colorActionDanger,
                      borderColor: colorActionDanger,
                      "&:hover": { backgroundColor: colorSurfaceDangerHover },
                    }}
                  >
                    {t("myOrganization.staffView.delete")}
                  </CustomButton>
                ) : undefined
              }
            >
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("myOrganization.staff.firstName")}
                    fullWidth
                    size="small"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("myOrganization.staff.lastName")}
                    fullWidth
                    size="small"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("myOrganization.staff.email")}
                    fullWidth
                    size="small"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("myOrganization.staff.phone")}
                    fullWidth
                    size="small"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      {t("myOrganization.staffView.assignRole")}
                    </InputLabel>
                    <Select
                      value={roleId}
                      label={t("myOrganization.staffView.assignRole")}
                      onChange={(e) => setRoleId(e.target.value)}
                    >
                      {(roles ?? []).map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {canManage && (
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <CustomLoadingButton
                      loading={updateMutation.isPending}
                      onSubmit={handleSave}
                      label={t("common.saveChanges")}
                      color="success"
                      fullwidth={false}
                    />
                  </Grid>
                )}
              </Grid>
            </InfoCard>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteMutation.isPending && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("myOrganization.staffView.deleteConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("myOrganization.staffView.deleteConfirmBody", {
              name: fullName,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setDeleteOpen(false)}
            disabled={deleteMutation.isPending}
          >
            {t("common.cancel")}
          </CustomButton>
          <CustomButton
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              backgroundColor: colorActionDanger,
              color: "#fff",
              "&:hover": { backgroundColor: colorTextError },
            }}
          >
            {t("common.delete")}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

export default StaffView;
